/**
 * Typed client for Connect's individual registrations
 * (workers/connect-api/src/handlers/registrations.ts).
 *
 * Registrants sign in with the shared magic link (the session cookie rides
 * same-origin fetch). Admin calls are Cloudflare Access-gated in production;
 * in local dev they carry the `x-admin-email` bypass header, compiled out of
 * production builds — the same pattern as lib/api.ts.
 */
import type { Program } from "@alight/contracts";
import { ApiError } from "./api";

export { ApiError };

export type RegistrationStatus = "registered" | "withdrawn";

export interface Registration {
  id: number;
  ref: string;
  program_id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  /** The location they said they'd attend, as the site labelled it when they
   *  registered (or "Not sure yet"). Blank for rows from before the question. */
  location: string;
  answers: Record<string, string>;
  status: RegistrationStatus;
  created_at: string;
  updated_at: string;
}

export interface MyProgramSummary {
  id: number;
  title: string;
  schedule: string;
  start_date: string | null;
  end_date: string | null;
  /** Registration still open — edits are accepted. */
  open: boolean;
}

export interface AdminProgramSummary {
  id: number;
  title: string;
  start_date: string | null;
  end_date: string | null;
  active: number;
}

export interface RegistrationInput {
  program_id?: number;
  first_name: string;
  last_name: string;
  phone?: string;
  location: string;
  answers: Record<string, string>;
}

export type AdminStatusFilter = RegistrationStatus | "all";

export interface AdminRegistrationsResult {
  program: Program | null;
  registrations: Registration[];
  counts: { registered: number; withdrawn: number };
  programs: AdminProgramSummary[];
}

/** An error that carries per-field messages from a 400 validation failure. */
export class RegistrationError extends ApiError {
  constructor(
    status: number,
    message: string,
    public fields: Record<string, string>,
  ) {
    super(status, message);
    this.name = "RegistrationError";
  }
}

const DEV_ADMIN_EMAIL =
  (import.meta.env.VITE_ADMIN_EMAIL as string | undefined) ?? "vc.le21@gmail.com";

function adminHeaders(): Record<string, string> {
  return import.meta.env.DEV ? { "x-admin-email": DEV_ADMIN_EMAIL } : {};
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; admin?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.admin) Object.assign(headers, adminHeaders());

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiError(0, "Network error — please check your connection and try again.");
  }

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }

  if (!res.ok) {
    const rec = json && typeof json === "object" ? (json as Record<string, unknown>) : {};
    const message = typeof rec.error === "string" && rec.error ? rec.error : `Request failed (${res.status})`;
    if (rec.fields && typeof rec.fields === "object") {
      throw new RegistrationError(res.status, message, rec.fields as Record<string, string>);
    }
    throw new ApiError(res.status, message);
  }
  return json as T;
}

/* ---- public / session ---------------------------------------------------- */

/** GET /api/connect/current-program */
export function getCurrentProgram(): Promise<{ program: Program | null; open: boolean }> {
  return request("GET", "/api/connect/current-program");
}

/** GET /api/connect/registrations/mine — 401 when signed out. */
export function getMyRegistrations(): Promise<{ registrations: Registration[]; programs: MyProgramSummary[] }> {
  return request("GET", "/api/connect/registrations/mine");
}

/** POST /api/connect/registrations — create or update (same program + email). */
export function saveRegistration(input: RegistrationInput): Promise<{ registration: Registration; created: boolean }> {
  return request("POST", "/api/connect/registrations", { body: input });
}

/** POST /api/connect/registrations/:id/withdraw */
export function withdrawRegistration(id: number): Promise<{ registration: Registration }> {
  return request("POST", `/api/connect/registrations/${id}/withdraw`);
}

/* ---- admin --------------------------------------------------------------- */

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== "");
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : "";
}

/** GET /api/admin/connect/registrations */
export function adminListRegistrations(params: {
  program_id?: number;
  status?: AdminStatusFilter;
  q?: string;
}): Promise<AdminRegistrationsResult> {
  return request("GET", `/api/admin/connect/registrations${qs(params)}`, { admin: true });
}

/** PATCH /api/admin/connect/registrations/:id */
export function adminSetRegistrationStatus(
  id: number,
  status: RegistrationStatus,
): Promise<{ registration: Registration }> {
  return request("PATCH", `/api/admin/connect/registrations/${id}`, { body: { status }, admin: true });
}

/** GET /api/admin/connect/registrations.csv — fetched (so the dev header and
 *  Access cookie ride along), then handed to the browser as a download. */
export async function adminDownloadRegistrationsCsv(programId?: number): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/admin/connect/registrations.csv${qs({ program_id: programId })}`, {
      headers: adminHeaders(),
      credentials: "same-origin",
    });
  } catch {
    throw new ApiError(0, "Network error — please check your connection and try again.");
  }
  if (!res.ok) throw new ApiError(res.status, `Couldn't download the CSV (${res.status})`);
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "registrations.csv";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
